import { toPerUnitDeduction } from "~/stores/kiosk-session/hooks/use-kiosk-products/to-per-unit-deduction";
import { type OrderingProductBasket } from "~/stores/kiosk-session/hooks/use-kiosk-products/types";

import { type CreateOrderV4AsyncArgs } from "../requests";

/**
 * Maps the v0-shaped ordering basket (the V3 payload) to the v4 basket schema.
 *
 * The server recomputes the order total from this payload and rejects the
 * order when it differs from the payment total, so every priced part of the
 * basket — including option choices — must survive the mapping.
 */
export function toV4Basket(
  basket: OrderingProductBasket[]
): CreateOrderV4AsyncArgs["basket"] {
  return basket.map<CreateOrderV4AsyncArgs["basket"][number]>((item) => ({
    type:
      item.type === "custom" || item.type === "variant-weight"
        ? "regular"
        : item.type,
    productLineId: item.productLineId,
    id: item.id,
    category: item.category,
    unitPriceExclVat: item.unitPrice,
    name: item.name ?? "",
    amount: item.amount,
    version: "v1" as const,
    taxRate: {
      id: item.taxRateId,
      name: item.taxRateName,
      vatRate: item.vatRate
    },
    options: item.options?.map((option) => ({
      id: option.id,
      choices: option.choices.map(({ unitPrice, ...choice }) => ({
        ...choice,
        ...(unitPrice !== undefined && { unitPriceExclVat: unitPrice })
      }))
    })),
    stampCardRuleId: item.stampCardRuleId,
    // Forwarded as declared: the server re-resolves the scheme and rejects the
    // order on mismatch, so inventing or reshaping anything here breaks it.
    deposit: item.deposit,
    discounts: [
      ...(item.discount
        ? [
            {
              ...item.discount,
              // V1 mapper multiplies by qty, so a unitPrice line total
              // must be divided back to per-unit.
              unitDiscountInclVat:
                item.discount.allowanceType === "unitPrice"
                  ? toPerUnitDeduction(item.discount.amount, item.amount)
                  : item.discount.amount
            }
          ]
        : []),
      ...(item.unitDiscountedPrice
        ? [
            {
              type: "discount-line" as const,
              id: "discount-line",
              name: "POS Line Discount",
              percentage: null,
              allowanceType: null,
              unitDiscountInclVat: item.unitDiscountedPrice
            }
          ]
        : [])
    ]
  }));
}
